import React, {ChangeEvent, FocusEvent, MouseEventHandler, ReactElement, ReactNode, Ref, SyntheticEvent} from "react";
import Select, {Accessors} from "../ui/Select/Select";
import Checkbox, {CheckboxClickEvent} from "../ui/Checkbox";
import Help from "../ui/Help/Help";
import TextField from "../ui/TextField";
import util from "./util";
import IconButton from "../ui/IconButton/IconButton";
import Icons from "../ui/IconButton/Icons";
import StateLabel from '../ui/StateLabel/StateLabel';
import DatePicker from '../ui/DateComponents/DatePicker/DatePicker';
import {isMobileView} from "./picos/browserFns";
import RoutedIconButton from "../ui/Buttons/RoutedIconButton";
import ErrorBoundaryO from "../ErrorBoundary/ErrorBoundaryO";
import {EntityType} from "@/@types/entity";
import type {ActionMeta, GroupBase, Options, OptionsOrGroups} from "react-select";
import type {SelectOption} from "@/@types/select";

export type InputOptions = {
    paramName: string, // used for both css type and name of param on current
    uiLabel: string | ReactElement, // label for the element. Can be a string or JSX
    type: 'text' | 'number' | 'money' | 'percentage' | 'numericText', // type of field to generate
    required?: boolean, // is this value required?
    placeholder?: string, // placeholder text (NOT default value)
    autoFocus?: boolean, // should this parameter be autoFocused?
    selectText?: boolean, // should the text be selected on focus?
    ref?: Ref<HTMLInputElement>, // reference for the input
    disabled?: boolean, // should the component start disabled?
    stateLabelItem?: EntityType, // which item is the <StateLabel> applied to?
    hidden?: boolean, // should this element be hidden?
    autocomplete?: string, // Should we use autocomplete on this field?
    keepLeadingZeros?: boolean, // Should we keep leading zeroes on a numeric field?
    secret?: boolean, // Should this field be marked as sensitive, e.g. a credit card number
    min?: number, // minimum value for number fields
    max?: number, // maximum value for number fields
    minLength?: number, // minimum length for text fields
    maxLength?: number, // maximum length for text fields
    inputMode?: 'numeric' | 'text' | 'tel' | 'email', // input mode for mobile keyboards
    pattern?: string, // regex pattern for validation
    error?: boolean, // is an error present?
}

export type MultilineOptions = {
    uiLabel?: string | ReactElement, // label for the element. Can be a string or JSX
    paramName?: string, // used for both css type and name of param on current
    required?: boolean, // is this value required?
    autoFocus?: boolean, // the JSX for the page
}

export type MultilineTextOnlyOptions = {
    data?: any, // Data to pass back in calls
    // ref?: Ref<HTMLTextAreaElement>, // ref to this field
    onBlur?: (e: FocusEvent<HTMLInputElement>, paramName: string, value: string, data: any) => void, // onBlur to call (override)
    forceRefreshCounter?: number, // A counter value to bust state and force a refresh
    disabled?: boolean, // do not allow editing
    richPaste?: boolean, // sanitize pasted HTML to a formatting allow-list instead of flattening to plain text (rich-text fields)
}

export type SelectOptions = {
    autoAdvance?: boolean,
    disablePortaling?: boolean,
    placeholder?: string,
    disabled?: boolean,
    stateLabelItem?: any,
    required?: boolean,
}

export type Getter<T, S> = (value?: any) => SelectOption<T, S> | SelectOption<T, S>[];

export type ReactElementWithState = ReactElement & { setState: Function }

export type AnyViewableType = ReactNode | ReactElement | string | number;
export type AnyViewable = AnyViewableType | AnyViewableType[];

/**
 * Remove leading zeroes from a string.
 *
 * @param {string} str
 */
function removeLeadingZeroes(str: string) {
    if (str === '.') return '0.';
    if (!str || str === '0' || str.startsWith('.') || str.startsWith('0.')) return str;

    return str.replace(/^0+/, '');
}

export type HandleTextChangeEvent = (e: ChangeEvent<HTMLInputElement>, field: string, textOverride?: string, plainText?: string) => void;
export type HandleBlurEvent = (e: FocusEvent<HTMLInputElement>, param: string, textOverride?: string, plainText?: string) => void | VoidFunction;

class FormElement {
    private readonly uiParent: string;
    private readonly current: any;
    private readonly handleTextChange: HandleTextChangeEvent;
    private readonly handleBlur: HandleBlurEvent;

    /**
     * Constructor for FormElement
     * @param {string} uiParent - CSS name for the parent
     * @param {any} current
     * @param {Function} handleTextChange
     * @param {Function} [handleBlur]
     */
    constructor(uiParent: string, current: any, handleTextChange: HandleTextChangeEvent, handleBlur?: HandleBlurEvent) {
        this.uiParent = uiParent;
        this.current = current;
        this.handleTextChange = handleTextChange;
        this.handleBlur = handleBlur || util.noop;
    }

    /**
     * Get a default text handler function for use with handleTextChange.
     *
     * @param {ReactElementWithState} component
     * @param {any} current
     * @return {Function}
     */
    static getTextHandler = (component: ReactElementWithState, current: any): HandleTextChangeEvent => {
        return (e: ChangeEvent<HTMLInputElement>, field: string, overrideText?: string) => {
            e.preventDefault();

            const clone = typeof current.clone === 'function' ? current.clone() : {...current};
            clone[field] = overrideText || e.target.value;

            component.setState({current: clone, hasChanged: true});
        };
    }

    /**
     * Render a UI element with a label and value.
     *
     * @param {string} paramValue - The name of the parameter on current object
     * @param {string|ReactElement} uiLabel - The label, either a string or JSX
     * @param {AnyViewable} value - The element to use for the value
     * @param {boolean} [required] - Is this a required field?
     */
    renderElement = (paramValue: string, uiLabel: ReactNode, value: AnyViewable, required?: boolean) => {
        // If a simple label, we will mark it with u-required, otherwise we use u-required-help to find the embedded label
        return <ErrorBoundaryO uniqueKey={`form-element-${paramValue}`} shouldUseErrorIcon={true}>
            <div className={`Row ${this.uiParent}__${paramValue}`} key={paramValue}>
                <div key={`FormComponent ${this.uiParent}__${paramValue}`}
                     className={`FormComponent ${this.uiParent}__${paramValue}`}
                >
                    <label htmlFor={paramValue}
                           className={`FormComponentLabel ${required ? typeof (uiLabel) === 'string' ? 'u-required' : 'u-required-help' : ''}`}>{uiLabel}
                    </label>
                    <div className="FormComponentValue">
                        {value}
                    </div>
                </div>
            </div>
        </ErrorBoundaryO>;
    };

    /**
     * Render a UI element with a label and value.
     *
     * @param {string} paramValue - The name of the parameter on current object
     * @param {string|ReactElement} uiLabel - The label, either a string or JSX
     * @param {ReactElement} value - The element to use for the value
     * @param {boolean} required - Is this element required?
     * @param {any} [ref] - optional ref
     * @returns {*}
     */
    renderElementNoWrap = (paramValue: string, uiLabel: string | ReactElement, value: ReactElement, required: boolean, ref?: any): any => {
        // If a simple label, we will mark it with u-required, otherwise we use u-required-help to find the embedded label
        return <ErrorBoundaryO uniqueKey={`form-element-no-wrap-${paramValue}`} shouldUseErrorIcon={true}>
            <div className="Row" key={paramValue}>
                <div className={`FormComponent ${this.uiParent}__${paramValue}`} ref={ref}>
                    {uiLabel && <label htmlFor={paramValue}
					                   className={`FormComponentLabel ${required ? typeof (uiLabel) === 'string' ? 'u-required' : 'u-required-help' : ''}`}>{uiLabel}</label>
                    }
                    <div className="FormComponentValue noWrap">
                        {value}
                    </div>
                </div>
            </div>
        </ErrorBoundaryO>;
    };

    /**
     * Render a checkbox.
     *
     * @param {string} paramName
     * @param {string|ReactElement} uiLabel
     * @param {boolean} disabled
     * @param {(e: SyntheticEvent) => void} onClick
     * @param {any} [data] - if extra data is to be passed back, put it here, and it will be returned in the click handler
     */
    renderCheckbox = (paramName: string, uiLabel: string | ReactElement, disabled: boolean, onClick: CheckboxClickEvent, data?: any) => {
        const _onClick: CheckboxClickEvent = data ? (e: SyntheticEvent) => onClick(e, data) : onClick;

        if (isMobileView()) return (
            <ErrorBoundaryO uniqueKey={`form-element-checkbox-${paramName}`} shouldUseErrorIcon={true}>
                <div className="Row" id={`${uiLabel}${paramName}`}>
                    <div className="FormComponentValue">
                        <Checkbox checked={util.getBoolean(this.current[paramName], false) || false}
                                  onClick={_onClick}
                                  disabled={disabled}
                                  label={uiLabel}/>
                    </div>
                </div>
            </ErrorBoundaryO>
        );

        else return this.renderElement(paramName, uiLabel, <Checkbox
            checked={util.getBoolean(this.current[paramName], false) || false}
            onClick={_onClick}
            disabled={disabled}
            label={paramName}
            hideLabel={true}
        />);
    };

    /**
     * Render a checkbox attached to a date.  Will be checked if a date is found, unchecked otherwise.
     * Includes a checkbox and a date field. The date field will be hidden if the date is null.
     * Click on the checkbox to get the current date/time and show the field.
     *
     * @param {string} paramName - this is the field name for the DATE. There is no field for on/off.
     * @param {string|ReactElement} uiLabel - only one label here, attached to the checkbox
     * @param {boolean} disabled - is the checkbox disabled?
     * @param {CheckboxClickEvent} onCheckboxClicked - method to call when the checkbox is clicked
     * @param {(date: Date) => void} onDateChanged - method to call when the date changes
     * @param {any} [data] - if extra data is to be passed back, put it here, and it will be returned in the click handler
     */
    renderDateWithCheckbox = (paramName: string, uiLabel: string | ReactElement, disabled: boolean, onCheckboxClicked: CheckboxClickEvent, onDateChanged: (date: Date) => void, data?: any) => {
        const _onClick: CheckboxClickEvent = data ? (e: SyntheticEvent) => onCheckboxClicked(e, data) : onCheckboxClicked;
        const value = this.current[paramName];
        const date = value ? new Date(value) : null;

        if (isMobileView()) return (
            <ErrorBoundaryO uniqueKey={`form-element-date-checkbox-${paramName}`} shouldUseErrorIcon={true}>
                <div className="Row ClassWithDate" id={`${uiLabel}${paramName}`}>
                    <div className="FormComponentValue">
                        <Checkbox checked={!!date || false}
                                  onClick={_onClick}
                                  disabled={disabled}
                                  label={uiLabel}
                                  data={data}/>
                        {value &&
							<DatePicker name={paramName} onChange={onDateChanged} defaultValue={date}/>
                        }
                    </div>
                </div>
            </ErrorBoundaryO>
        );

        else return this.renderElement(paramName, uiLabel,
            <>
                <Checkbox checked={!!date || false}
                          onClick={_onClick}
                          disabled={disabled}
                          label={paramName}
                          hideLabel={true}
                          data={data}
                />
                {value &&
					<DatePicker name={paramName} onChange={onDateChanged} defaultValue={date}/>
                }
            </>
        );
    };


    /**
     * Render a checkbox where the box is in FRONT.
     *
     * @param {string} paramName
     * @param {string|ReactElement} uiLabel
     * @param {boolean} disabled
     * @param {MouseEventHandler} onClick
     * @param {Function} [transform] - If this is not a boolean value, give a transform function
     */
    renderReverseCheckbox = (paramName: string, uiLabel: string | ReactElement, disabled: boolean, onClick: MouseEventHandler, transform?: Function) => {
        const checked = transform
                        ? transform(this.current[paramName])
                        : (this.current[paramName] || false);

        return <ErrorBoundaryO uniqueKey={`form-element-reverse-checkbox-${paramName}`} shouldUseErrorIcon={true}>
            <div className="Row" key={`${uiLabel}${paramName}`}>
                <div className="FormComponentValue">
                    <Checkbox checked={checked} onClick={onClick} disabled={disabled} label={uiLabel} reversed/>
                </div>
            </div>
        </ErrorBoundaryO>;
    };

    /**
     * Render a date field with a blur method.
     *
     * @param {string} paramName
     * @param {string|ReactElement} uiLabel
     * @param {Function} onBlur
     * @param {boolean} [required]
     */
    renderDate = (paramName: string, uiLabel: string | ReactElement, onBlur: (date: Date) => void, required?: boolean) => {
        const value = this.current[paramName];
        const date = value ? new Date(value) : null;

        return this.renderElement(paramName, uiLabel,
            <DatePicker name={paramName}
                        onChange={onBlur}
                        defaultValue={date}
                        ariaLabel={typeof (uiLabel) === 'string' ? uiLabel : paramName}
                        required={required}
            />, required);
    };

    /**
     * Render a date/time field with a blur method.
     *
     * @param {string} paramName
     * @param {string|ReactElement} uiLabel
     * @param {Function} onBlur
     * @param {boolean} _ - was showTime, no longer used
     * @param {boolean} required
     */
    renderDateTime = (paramName: string, uiLabel: string | ReactElement, onBlur: (date: Date) => void, _: boolean, required: boolean) => {
        const value = this.current[paramName];

        return this.renderElement(paramName, uiLabel, (
            <DatePicker name={paramName}
                        defaultValue={value}
                        onChange={onBlur}
                        ariaLabel={typeof (uiLabel) === 'string' ? uiLabel : paramName}
                        useTime={true}
                        required={required}
            />
        ), required);
    };

    /**
     * @param {string} paramName - used for both css type and name of param on current
     * @param {string|ReactElement} uiLabel - label for the element. Can be a string or JSX
     * @param {'text'|'number'|'money'|'percentage'} type - either text or number
     * @param {boolean} required - is this value required
     * @param {string} [placeholder]
     * @param {number} [minValue] - override the input's min attribute. For 'money' inputs the default is -9999999999.99; pass 0 to disallow negatives.
     * @return {ReactElement} - the JSX for the page
     */
    renderInput = (paramName: string, uiLabel: string | ReactElement, type: 'text' | 'number' | 'money' | 'percentage', required: boolean, placeholder?: string, minValue?: number): ReactElement => {
        const value = this.current[paramName];
        const isNull = value == null;
        const moneyStyle = {borderBottomLeftRadius: 0, borderTopLeftRadius: 0};
        const percentStyle = {borderBottomRightRadius: 0, borderTopRightRadius: 0};
        const disallowNegative = minValue !== undefined && minValue >= 0;

        const onNumericChange = (e: ChangeEvent<HTMLInputElement>) => {
            if (e.target.value === '') e.target.value = "0";
            else e.target.value = removeLeadingZeroes(e.target.value);

            // Clamp out-of-range values pasted or set programmatically.
            if (minValue !== undefined && parseFloat(e.target.value) < minValue) {
                e.target.value = String(minValue);
            }

            this.handleTextChange(e, paramName);
        }

        const onNumericBlur = (e: FocusEvent<HTMLInputElement>) => {
            e.persist();
            if (e.target.value === '') e.target.value = "0";
            else e.target.value = removeLeadingZeroes(e.target.value);

            this.handleBlur(e, paramName);
        }

        const onNumericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            // Block typing a minus sign when the input is constrained to non-negative values.
            if (disallowNegative && (e.key === '-' || e.key === 'Subtract')) {
                e.preventDefault();
            }
        };

        if (type === 'money') {
            const moneyMin = minValue !== undefined ? String(minValue) : "-9999999999.99";
            return this.renderElementNoWrap(paramName, uiLabel, (<>
                <div className="dollar-sign">$</div>
                <input className='money'
                       style={moneyStyle}
                       key={paramName}
                       value={isNull ? 0 : value}
                       autoFocus={false}
                       type="number" step="0.01" min={moneyMin} max="9999999999.99"
                       required={required}
                       onChange={onNumericChange}
                       onBlur={onNumericBlur}
                       onKeyDown={onNumericKeyDown}
                       placeholder={placeholder}
                />
            </>), required);
        }
        else if (type === 'percentage') {
            return this.renderElementNoWrap(paramName, uiLabel, (<>
                <input className='percentage'
                       key={paramName}
                       style={percentStyle}
                       value={isNull ? '' : value}
                       autoFocus={false}
                       type="number" step="0.01" min="0" max="100"
                       required={required}
                       onChange={onNumericChange}
                       onBlur={onNumericBlur}
                       placeholder={placeholder}
                />
                <div className="percentage-sign">%</div>
            </>), required);
        }
        else if (type === 'number') {
            return this.renderElementNoWrap(paramName, uiLabel, (<>
                <input defaultValue={this.current[paramName] ?? null}
                       type="number" step="1" min="0"
                       required={required}
                       onChange={onNumericChange}
                       onBlur={onNumericBlur}
                       placeholder={placeholder}
                />
            </>), required);
        }
        else return this.renderElement(paramName, uiLabel, (<input key={paramName}
                                                                   value={isNull ? '' : value}
                                                                   autoFocus={false}
                                                                   type={type}
                                                                   required={required}
                                                                   onChange={(e) => this.handleTextChange(e, paramName)}
                                                                   onBlur={e => this.handleBlur(e, paramName)}
                                                                   placeholder={placeholder}
            />), required);
    };


    /**
     * @param {string} paramName - used for both css type and name of param on current
     * @param {string|ReactElement} uiLabel - label for the element. Can be a string or JSX
     * @param {boolean} required - is this value required
     * @param {any} [options={}] - options
     * @param {any} [options.data] - Data to pass back in calls
     * @param {any} [options.ref] - ref to this field
     * @param {boolean}  [options.stripHtml] - should we strip the html from this field?
     * @return {ReactElement} - the JSX for the page
     */
    renderMultiline = (paramName: string, uiLabel: string | ReactElement, required: boolean, options?: { data?: any, ref?: any, stripHtml?: boolean }): ReactElement => {
        return this.renderElement(paramName, uiLabel, (
            <TextField defaultValue={this.current[paramName] || ''}
                       autoFocus={false}
                       data={options?.data}
                       required={required}
                       onChange={(e: ChangeEvent<HTMLInputElement>, _: any, text: any, plainText: string) => this.handleTextChange(e, paramName, text, plainText)}
                       onBlur={(e: FocusEvent<HTMLInputElement>, data: any, value: any) => this.handleBlur(e, paramName, value, data)}
                       ref={options?.ref}
                       stripHtml={options?.stripHtml ?? false}
            />), required);
    };

    /**
     * @param {string} paramName - used for both css type and name of param on current
     * @param {string|ReactElement} uiLabel - label for the element. Can be a string or JSX
     * @param {boolean} required - is this value required
     * @param {any} [options={}] - options
     * @param options.data - Data to pass back in calls
     * @param options.ref - ref to this field
     * @param options.onBlur - onBlur to call (override)
     * @param options.forceRefreshCounter
     * @param options.disabled - do not allow editing
     * @return {ReactElement} - the JSX for the page
     */
    renderMultilineTextOnly = (paramName: string, uiLabel: string | ReactElement, required: boolean, options: MultilineTextOnlyOptions = {}): ReactElement => {
        const handleBlur = options?.onBlur
                           ? (e: FocusEvent<HTMLInputElement>, data: any, value: string) => options.onBlur(e, paramName, value, data)
                           : (e: FocusEvent<HTMLInputElement>, data: any, value: string) => this.handleBlur(e, paramName, value, data);

        return this.renderElement(paramName, uiLabel, (
            <TextField defaultValue={this.current[paramName] || ''}
                       autoFocus={false}
                       data={options?.data}
                       required={required}
                       onChange={(e: ChangeEvent<HTMLInputElement>, _: any, text: string, plainText: string) => this.handleTextChange(e, paramName, text, plainText)}
                       onBlur={handleBlur}
                       stripHtml={true}
                       richPaste={options?.richPaste}
                       forceRefreshCounter={options?.forceRefreshCounter}
                       disabled={options?.disabled}
            />), required);
    };

    /**
     * @param {MultilineOptions} options
     */
    renderMultilineOptions = (options: MultilineOptions): ReactElement => {
        const {paramName, uiLabel, required, autoFocus} = options;

        return this.renderElement(paramName, uiLabel, (<>
            <TextField defaultValue={this.current[paramName] || ''}
                       autoFocus={autoFocus}
                       required={required}
                       onChange={(e: ChangeEvent<HTMLInputElement>, _: any, text: string, plainText: string) => this.handleTextChange(e, paramName, text, plainText)}
                       onBlur={(e: FocusEvent<HTMLInputElement>) => this.handleBlur(e, paramName)}
            />
        </>), required);
    };

    /**
     * Render an <input> element, passing through the options specified.
     *
     * @param {InputOptions} options
     */
    renderInputOptions = (options: InputOptions): ReactElement => {
        const {
            paramName,
            uiLabel,
            type,
            required,
            placeholder,
            autoFocus,
            selectText,
            ref,
            disabled,
            stateLabelItem,
            hidden,
            autocomplete,
            keepLeadingZeros,
            secret,
            minLength,
            maxLength,
            inputMode,
            pattern,
            error
        } = options;

        let result: any;

        const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            if (selectText) e.target?.select?.();
        }

        const onBlur = (e: FocusEvent<HTMLInputElement>) => {
            this.handleBlur(e, paramName);
        }

        const onTextChange = (e: ChangeEvent<HTMLInputElement>) => {
            this.handleTextChange(e, paramName);
        }

        const onNumericChange = (e: ChangeEvent<HTMLInputElement>) => {
            if (e.target.value === '') e.target.value = "0";
            else if (!keepLeadingZeros) e.target.value = removeLeadingZeroes(e.target.value);

            this.handleTextChange(e, paramName);
        }

        const onNumericBlur = (e: FocusEvent<HTMLInputElement>) => {
            if (e.target.value === '') e.target.value = "";
            else e.target.value = removeLeadingZeroes(e.target.value);

            this.handleBlur(e, paramName);
        }

        if (type === 'money') {
            result = this.renderElement(paramName, uiLabel, (<>
                <div className="dollar-sign">$</div>
                <input
                    className='money'
                    ref={ref}
                    defaultValue={this.current[paramName] ?? ''}
                    autoFocus={autoFocus}
                    type="number" step="0.01" min="-9999999999.99" max="9999999999.99"
                    required={required}
                    onChange={onNumericChange}
                    onBlur={onNumericBlur}
                    onFocus={onFocus}
                    placeholder={placeholder}
                    data-lpignore={autocomplete ? null : "true"}
                    disabled={disabled}
                    data-private={hidden ? "true" : undefined}
                    autoComplete={autocomplete}
                />
            </>), required);
        }
        else if (type === 'percentage') {
            result = this.renderElementNoWrap(paramName, uiLabel, (<>
                <input
                    className='percentage'
                    ref={ref}
                    defaultValue={this.current[paramName] ?? ''}
                    autoFocus={autoFocus}
                    type="number" step="0.01" min="0" max="100"
                    required={required}
                    onChange={onNumericChange}
                    onBlur={onNumericBlur}
                    onFocus={onFocus}
                    placeholder={placeholder}
                    data-lpignore={autocomplete ? null : "true"}
                    disabled={disabled}
                    data-private={hidden ? "true" : undefined}
                    autoComplete={autocomplete}
                />
                <div className="percentage-sign">%</div>
            </>), required);
        }
        else if (type === 'number') {
            result = this.renderElementNoWrap(paramName, uiLabel, (<>
                <input
                    ref={ref}
                    defaultValue={this.current[paramName] ?? ''}
                    autoFocus={autoFocus}
                    type={secret ? "password" : "number"} step="1" min="0"
                    required={required}
                    onChange={onNumericChange}
                    onBlur={onNumericBlur}
                    onFocus={onFocus}
                    placeholder={placeholder}
                    data-lpignore={autocomplete ? null : "true"}
                    disabled={disabled}
                    data-private={hidden ? "true" : undefined}
                    autoComplete={autocomplete}
                    aria-invalid={error ? "true" : "false"}
                    aria-describedby={(error && autocomplete === "cc-name") ? "card-error" : undefined}
                />
            </>), required);
        }
        // numericText: text input that only accepts numeric characters (preserves leading zeros)
        else if (type === 'numericText') {
            result = this.renderElementNoWrap(paramName, uiLabel, (<>
                <input
                    ref={ref}
                    defaultValue={this.current[paramName] ?? ''}
                    autoFocus={autoFocus}
                    type={secret ? "password" : "text"}
                    inputMode={inputMode ?? "numeric"}
                    pattern={pattern ?? "[0-9]*"}
                    minLength={minLength}
                    maxLength={maxLength}
                    required={required}
                    onChange={onTextChange}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    placeholder={placeholder}
                    data-lpignore={autocomplete ? null : "true"}
                    disabled={disabled}
                    data-private={hidden ? "true" : undefined}
                    autoComplete={autocomplete}
                    aria-invalid={error ? "true" : "false"}
                />
            </>), required);
        }
        else result = this.renderElement(paramName, uiLabel, (<input //key={this.current[paramName] || ''}
                value={this.current[paramName] || ''}
                ref={ref}
                autoFocus={autoFocus}
                type={secret ? 'password' : type}
                required={required}
                onChange={onTextChange}
                onBlur={onBlur}
                onFocus={onFocus}
                placeholder={placeholder}
                data-lpignore={autocomplete ? null : "true"}
                disabled={disabled}
                data-private={hidden ? "true" : undefined}
                autoComplete={autocomplete}
            />), required);

        if (stateLabelItem) return <StateLabel item={stateLabelItem}>{result}</StateLabel>;
        else return result;
    };

    /**
     * @param {string} paramName - used for both css type and name of param on current
     * @param {string|ReactElement} uiLabel - label for the element. Can be a string or JSX
     * @param {boolean} multi - are multiple selections allowed
     * @param {boolean} allowNone - is a blank value ok?
     * @param {Getter} getter - function to return all values if no parameter sent. With param, limit to elements with
     *                            ids in the array
     * @param {Function} onChange - function to call when a change is made.  Returns currently selected items.
     * @param {boolean} usesExtraFields - set if you are using a data field to hold the object. Usually happens when the label is an Element
     * @param {SelectOptions} options - Additional options for the select component
     * @return {ReactElement} - the JSX for the page
     */
    renderSelect = <T, S>(paramName: string, uiLabel: string | ReactElement, multi: boolean, allowNone: boolean, getter: Getter<T, S>,
                          onChange: (newValue: any, actionMeta: ActionMeta<any>) => void, usesExtraFields: boolean = false, options: SelectOptions = {}
    ): ReactElement => {
        const result = this.renderElement(
            paramName,
            uiLabel,
            <Select name={paramName}
                    allowNone={allowNone}
                    autoAdvance={options?.autoAdvance}
                    defaultValue={getter(this.current)}
                    controlled={!multi}
                    isMulti={multi}
                    options={getter() as SelectOption<T, S>[]}
                    onChange={onChange}
                    tabSelectsValue={true}
                    usesExtraFields={usesExtraFields}
                    disablePortaling={options?.disablePortaling}
                    placeholder={options?.placeholder || 'Select...'}
                    disabled={options?.disabled}
                    required={options?.required}
            />,
            !allowNone
        );

        if (options?.stateLabelItem) return <StateLabel item={options?.stateLabelItem}>{result}</StateLabel>;
        else return result;
    };

    /**
     * @param {string} paramName - used for both css type and name of param on current
     * @param {string|ReactElement} uiLabel - label for the element. Can be a string or JSX
     * @param {string} route - The route to send to on config button click
     * @param {boolean} multi - are multiple selections allowed
     * @param {boolean} allowNone - is a blank value ok?
     * @param {Getter} getter - function to return all values if no parameter sent. With param, limit to elements with
     *                            ids in the array
     * @param {Function} onChange - function to call when a change is made.  Returns currently selected items.
     * @param {boolean} usesExtraFields - set if you are using a data field to hold the object. Usually happens when the label is an Element
     * @param {any} [options]
     * @param {boolean} [options.disablePortaling]
     * @param {string} [options.placeholder]
     * @param {string} [options.disabled]
     * @param {boolean} [options.required]
     * @return {ReactElement} - the JSX for the page
     */
    renderSelectWithConfig = <T, S>(paramName: string, uiLabel: string | ReactElement, route: string, multi: boolean, allowNone: boolean, getter: Getter<T, S>,
                                    onChange: (newValue: any, actionMeta: ActionMeta<any>) => void, usesExtraFields: boolean = false, options: SelectOptions = {}): ReactElement => {
        return this.renderElement(paramName, uiLabel, (<div className='SelectWithConfig'>
            <Select name={paramName}
                    allowNone={allowNone}
                    defaultValue={getter(this.current)}
                    isMulti={multi}
                    options={getter() as SelectOption<T, S>[]}
                    onChange={onChange}
                    tabSelectsValue={true}
                    usesExtraFields={usesExtraFields}
                    disablePortaling={options?.disablePortaling}
                    placeholder={options?.placeholder || 'Select...'}
                    disabled={options?.disabled}
                    required={options?.required}
            />
            {route ? <RoutedIconButton type={Icons.CONFIG} title='Configure' route={route}/>
                   : <IconButton type={Icons.CONFIG} title='Configure'/>}

        </div>), !allowNone);
    };

    /**
     * @param {string} paramName - used for both css type and name of param on current
     * @param {string|ReactElement} uiLabel - label for the element. Can be a string or JSX
     * @param {boolean} multi - are multiple selections allowed
     * @param {boolean} allowNone - is a blank value ok?
     * @param {Getter} getter - function to return all values if no parameter sent. With param, limit to elements with
     *                            ids in the array
     * @param {Function} onChange - function to call when a change is made.  Returns currently selected items.
     * @param {Function} isValid - callback to see if the new item is allowable
     * @param {Function} onCreate - callback when creating a new element
     * @param {boolean} usesExtraFields
     * @param {any} [options]
     * @param {boolean} options.disablePortaling
     * @param {string} options.placeholder
     * @return {ReactElement} - the JSX for the page
     */
    renderEditableSelect = <T, S>(paramName: string, uiLabel: string | ReactElement, multi: boolean, allowNone: boolean, getter: Getter<T, S>,
                                  onChange: (newValue: any, actionMeta: ActionMeta<any>) => void,
                                  isValid: (inputValue: string, value: Options<any>, options: OptionsOrGroups<any, GroupBase<any>>, accessors: Accessors<any>) => boolean,
                                  onCreate: (inputValue: string) => void, usesExtraFields: boolean, options?: SelectOptions
    ): ReactElement => {
        /**
         * Always allow new (non-duplicate) entry
         *
         * @returns {boolean}
         */
        const alwaysValid = (entry: any): boolean => {
            return !!entry;
        };

        return this.renderElement(paramName, uiLabel, (<Select key={paramName}
                                                               name={paramName}
                                                               allowNone={allowNone}
                                                               defaultValue={getter(this.current)}
                                                               isMulti={multi}
                                                               options={getter() as SelectOption<T, S>[]}
                                                               onChange={onChange}
                                                               tabSelectsValue={true}
                                                               allowCreate={true}
                                                               isValidNewOption={isValid || alwaysValid}
                                                               onCreateOption={onCreate}
                                                               usesExtraFields={usesExtraFields}
                                                               disablePortaling={options?.disablePortaling}
                                                               placeholder={options?.placeholder || 'Select...'}
        />), !allowNone);
    };

    /**
     * Render a label with Help
     *
     * @param {string} uiLabel
     * @param {ReactElement} content
     * @param {string} [title]
     * @returns {ReactElement}
     */
    renderHelpLabel = (uiLabel: string, content: ReactElement, title?: string): ReactElement => {
        return <ErrorBoundaryO uniqueKey={`form-element-help${uiLabel}`} shouldUseErrorIcon={true}>
            <div className='help__label'>
                <div className="help__label-text">{uiLabel}</div>
                <Help message={content} title={title || uiLabel}/>
            </div>
        </ErrorBoundaryO>;
    };
}

export default FormElement;
